import { Controller } from "@hotwired/stimulus"
import { apiHeaders, csrfToken, htmlHeaders, notifyOfflineWrite, parseIntOrNull, responseErrorMessage, showFormError, writeFetch } from "./sweeper_shared"

export default class extends Controller {
  static values = {
    url: String,
    formUrl: String,
    detailUrl: String,
    initialRoomId: Number
  }

  connect() {
    this.rooms = []
    this.detailId = this.hasInitialRoomIdValue && this.initialRoomIdValue > 0 ? this.initialRoomIdValue : null
    this.onActivated = this.render.bind(this)
    document.addEventListener("sweeper:rooms-activated", this.onActivated)
    this.dataLoaded = this.loadRooms().then(rooms => { this.rooms = rooms })
    if (this.detailId) this.openDetail(this.detailId, { replaceHistory: true })
  }

  disconnect() {
    document.removeEventListener("sweeper:rooms-activated", this.onActivated)
  }

  new() {
    this.detailId = null
    this.writeRoomState(null)
    this.openForm(null)
  }

  async card(event) {
    const card = event.target.closest(".room-card")
    if (!card) return

    const action = event.target.closest("[data-room-action]")?.dataset.roomAction || "open"
    const id = parseInt(card.dataset.roomId, 10)
    if (!Number.isFinite(id)) return

    const rooms = await this.ensureRooms()
    if (action === "open") this.openDetail(id)
    else if (action === "edit") this.openForm(rooms.find(room => room.id === id))
    else if (action === "up") this.move(id, -1)
    else if (action === "down") this.move(id, 1)
    else if (action === "delete") this.delete(id)
  }

  async render() {
    if (this.detailId) return this.openDetail(this.detailId)

    this.rooms = await this.loadRooms()
    if (this.element.getAttribute("src") === this.urlValue && typeof this.element.reload === "function") this.element.reload()
    else this.element.setAttribute("src", this.urlValue)
  }

  async loadRooms() {
    const response = await fetch("/api/v1/rooms", { credentials: "include", headers: apiHeaders() })
    return response.ok ? await response.json() : []
  }

  async ensureRooms() {
    if (this.rooms.length === 0) await (this.dataLoaded || this.loadRooms().then(rooms => { this.rooms = rooms }))
    return this.rooms
  }

  async move(id, direction) {
    const order = this.rooms.map(room => room.id)
    const index = order.indexOf(id)
    const target = index + direction
    if (index < 0 || target < 0 || target >= order.length) return

    ;[order[index], order[target]] = [order[target], order[index]]
    const response = await writeFetch("/api/v1/rooms/reorder", {
      method: "POST",
      credentials: "include",
      headers: apiHeaders(),
      body: JSON.stringify({ order })
    })
    if (!response.ok) return notifyOfflineWrite()

    this.render()
  }

  async delete(id) {
    const room = this.rooms.find(existing => existing.id === id)
    if (!confirm(`Verwijder ruimte "${room?.name || ""}"?`)) return

    const response = await writeFetch(`/api/v1/rooms/${id}`, { method: "DELETE", credentials: "include", headers: apiHeaders() })
    if (!response.ok) return notifyOfflineWrite()

    this.render()
  }

  async openForm(existing) {
    this.detailId = null
    this.writeRoomState(null)
    this.content.innerHTML = '<div class="empty-state">Laden...</div>'
    const url = new URL(this.formUrlValue, window.location.origin)
    if (existing?.id) url.searchParams.set("id", existing.id)
    const response = await fetch(url, {
      credentials: "include",
      headers: htmlHeaders("view-rooms")
    })
    if (!response.ok) {
      this.content.innerHTML = '<div class="empty-state">Formulier laden mislukt.</div>'
      return
    }

    this.content.innerHTML = await response.text()
    document.getElementById("rm-cancel").addEventListener("click", () => this.render())
    document.getElementById("rm-save").addEventListener("click", () => this.saveForm())
  }

  async saveForm() {
    const id = document.getElementById("rm-id")?.value
    const payload = {
      name: document.getElementById("rm-name").value.trim(),
      capacity: parseIntOrNull(document.getElementById("rm-capacity").value),
      seats: parseIntOrNull(document.getElementById("rm-seats").value),
      notes: document.getElementById("rm-notes").value
    }
    if (!payload.name) return showFormError("rm-error", "Naam is verplicht")

    const response = await writeFetch(id ? `/api/v1/rooms/${id}` : "/api/v1/rooms", {
      method: id ? "PATCH" : "POST",
      credentials: "include",
      headers: apiHeaders(),
      body: JSON.stringify(payload)
    })
    if (!response.ok) {
      showFormError("rm-error", await responseErrorMessage(response, "Opslaan mislukt"))
      return
    }
    this.render()
  }

  async openDetail(id, options = {}) {
    this.detailId = id
    this.content.innerHTML = '<div class="empty-state">Laden...</div>'
    const response = await fetch(this.detailUrl(id), {
      credentials: "include",
      headers: htmlHeaders("view-rooms")
    })
    if (!response.ok) {
      this.content.innerHTML = '<div class="empty-state">Niet gevonden.</div>'
      this.detailId = null
      return
    }

    this.content.innerHTML = await response.text()
    this.writeRoomState(id, options)
    document.getElementById("btn-rooms-back").addEventListener("click", () => {
      this.detailId = null
      this.writeRoomState(null)
      this.render()
    })
    document.getElementById("btn-room-edit").addEventListener("click", () => {
      this.detailId = null
      this.openForm({ id })
    })
    this.bindGallery(id)
  }

  bindGallery(roomId) {
    this.content.querySelectorAll('[data-room-gallery-action="cover"]').forEach(button => {
      button.addEventListener("click", async () => {
        const response = await writeFetch(`/api/v1/rooms/${roomId}/set_cover`, {
          method: "PATCH",
          credentials: "include",
          headers: apiHeaders(),
          body: JSON.stringify({ attachment_id: button.dataset.id })
        })
        if (!response.ok) return notifyOfflineWrite()

        this.openDetail(roomId)
      })
    })
    this.content.querySelectorAll('[data-room-gallery-action="remove"]').forEach(button => {
      button.addEventListener("click", async () => {
        if (!confirm("Afbeelding verwijderen?")) return
        const response = await writeFetch(`/api/v1/rooms/${roomId}/images/${button.dataset.id}`, {
          method: "DELETE",
          credentials: "include",
          headers: apiHeaders()
        })
        if (!response.ok) return notifyOfflineWrite()

        this.openDetail(roomId)
      })
    })
    this.content.querySelectorAll("[data-caption-id]").forEach(input => {
      let timeout
      const save = async () => {
        const response = await writeFetch(`/api/v1/rooms/${roomId}/images/${input.dataset.captionId}/caption`, {
          method: "PATCH",
          credentials: "include",
          headers: apiHeaders(),
          body: JSON.stringify({ caption: input.value })
        })
        if (!response.ok) notifyOfflineWrite()
      }
      input.addEventListener("input", () => {
        clearTimeout(timeout)
        timeout = setTimeout(save, 500)
      })
      input.addEventListener("blur", () => {
        clearTimeout(timeout)
        save()
      })
      input.addEventListener("click", event => event.stopPropagation())
    })
    document.getElementById("room-image-upload")?.addEventListener("change", async event => {
      const file = event.target.files[0]
      if (!file) return
      const body = new FormData()
      body.append("image", file)
      const response = await writeFetch(`/api/v1/rooms/${roomId}/upload_image`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": csrfToken() },
        body
      })
      if (!response.ok) return notifyOfflineWrite(await responseErrorMessage(response, "Upload mislukt"))

      this.openDetail(roomId)
    })
  }

  get content() {
    return document.getElementById("rooms-content")
  }

  detailUrl(id) {
    return this.detailUrlValue.replace("__ID__", encodeURIComponent(id))
  }

  writeRoomState(id, { replaceHistory = false } = {}) {
    if (window.location.pathname !== "/rooms" && window.location.pathname !== "/sweeper/rooms") return

    const params = new URLSearchParams(window.location.search)
    if (id) params.set("room", id)
    else params.delete("room")

    const query = params.toString()
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}`
    if (nextUrl === `${window.location.pathname}${window.location.search}`) return

    window.history[replaceHistory ? "replaceState" : "pushState"]({ view: "rooms", room: id }, "", nextUrl)
  }
}
